/**
 * LOCAL model pricing for the Custom Bid Tabs extraction cost ledger — USD per
 * 1,000,000 tokens.
 *
 * Kept HERE (not imported from `@edgevanta/ai`'s TOKEN_PRICING) on purpose: `ai`
 * depends on `bid-tab-extract`, so importing back would be a dependency cycle. It
 * is a handful of constants for the models Core-2 actually calls; keeping them
 * local keeps this package self-contained (the same reason it stays Sentry-free
 * and store-abstracted). When Anthropic/Google change a price, edit this one map.
 *
 * Only models used by Core-2 and Custom Bid Tabs normalization are listed. An
 * unknown model returns null so the ledger never records a fabricated number.
 */

/** USD per 1M tokens in each disjoint provider-usage bucket. */
export interface ModelPrice {
  input: number;
  output: number;
  cacheWrite: number;
  cacheRead: number;
}

const SONNET_5_STANDARD_PRICE_AT_MS = Date.parse('2026-09-01T00:00:00.000Z');
const SONNET_5_STANDARD_PRICE: ModelPrice = {
  input: 3.0,
  output: 15.0,
  cacheWrite: 3.75,
  cacheRead: 0.3,
};

/** Models are keyed by the exact id passed to the provider SDK. */
export const BID_TAB_MODEL_PRICING: Record<string, ModelPrice> = {
  'claude-sonnet-5': {
    input: 2.0,
    output: 10.0,
    cacheWrite: 2.5,
    cacheRead: 0.2,
  },
  'claude-sonnet-4-6': {
    input: 3.0,
    output: 15.0,
    cacheWrite: 3.75,
    cacheRead: 0.3,
  },
  'gemini-2.5-flash': {
    input: 0.3,
    output: 2.5,
    cacheWrite: 0.375,
    cacheRead: 0.03,
  },
  'gpt-4.1-mini': {
    input: 0.4,
    output: 1.6,
    cacheWrite: 0.4,
    cacheRead: 0.1,
  },
};

/** USD cost of one call, or null when the model has no price entry (records the
 *  tokens without inventing a dollar figure). Rounded to 6 dp — sub-cent reads
 *  (a warm-cache totals read) still carry a meaningful value. */
export function costUsd(options: {
  model: string;
  uncachedInputTokens: number;
  outputTokens: number;
  cacheWriteTokens?: number;
  cacheReadTokens?: number;
  at?: Date;
}): number | null {
  const {
    model,
    uncachedInputTokens,
    outputTokens,
    cacheWriteTokens = 0,
    cacheReadTokens = 0,
    at = new Date(),
  } = options;
  const price =
    model === 'claude-sonnet-5' && at.getTime() >= SONNET_5_STANDARD_PRICE_AT_MS
      ? SONNET_5_STANDARD_PRICE
      : BID_TAB_MODEL_PRICING[model];
  if (!price) {
    return null;
  }
  const usd =
    (uncachedInputTokens / 1_000_000) * price.input +
    (cacheWriteTokens / 1_000_000) * price.cacheWrite +
    (cacheReadTokens / 1_000_000) * price.cacheRead +
    (outputTokens / 1_000_000) * price.output;
  return Math.round(usd * 1_000_000) / 1_000_000;
}
